import React, { useState, useEffect, useRef } from "react";
import { Empty, FormInstance } from "antd";
import { useTranslation } from "react-i18next";
import { OpForm } from "components/customAntd/DLS/OpForm/OpForm";
import * as kioskApi from "../services/kioskApi";

interface VisitDetailsPageProps {
  form: FormInstance;
  token: string;
  locationId: number;
  workflowInFields?: any;
  workflowInFieldsCustom?: any[];
  selectedScheduledVisitor?: any;
  hosts?: any[];
  onLoadingChange?: (loading: boolean) => void;
}

const VisitDetailsPage: React.FC<VisitDetailsPageProps> = ({
  form,
  token,
  locationId,
  workflowInFields,
  workflowInFieldsCustom = [],
  selectedScheduledVisitor,
  hosts: propHosts = [],
  onLoadingChange,
}) => {
  const { t } = useTranslation();
  const [hosts, setHosts] = useState<any[]>(propHosts);
  const [initialValues, setInitialValues] = useState<any>({});
  const [hasInitializedForm, setHasInitializedForm] = useState(false);
  const formContainerRef = useRef<HTMLDivElement>(null);
  const lastVisitorIdRef = useRef<any>(null);

  const formatHosts = (hostList: any[] = []) => {
    return hostList
      .map((host: any) => {
        if (host.label && host.value) {
          const label =
            host.department &&
            !String(host.label).includes(`(${host.department})`)
              ? `${host.label} (${host.department})`
              : host.label;

          return {
            ...host,
            label,
            value: host.value?.toString() || "",
          };
        }

        const name =
          `${host.firstName || ""} ${host.lastName || ""}`.trim() || "Unknown";
        const label = host.department ? `${name} (${host.department})` : name;

        return {
          label,
          value: host.userId?.toString() || host.value?.toString() || "",
          email: host.email,
          department: host.department,
          title: host.title,
        };
      })
      .filter((host: any) => host.value);
  };

  // Reset initialization flag when selectedScheduledVisitor changes
  useEffect(() => {
    const currentVisitorId =
      selectedScheduledVisitor?.id || selectedScheduledVisitor?.visitId;
    if (currentVisitorId !== lastVisitorIdRef.current) {
      lastVisitorIdRef.current = currentVisitorId;
      setHasInitializedForm(false);
    }
  }, [selectedScheduledVisitor]);

  // Fetch hosts if not provided from parent
  useEffect(() => {
    const fetchHostsIfNeeded = async () => {
      // If hosts already provided from parent, use them
      if (propHosts && propHosts.length > 0) {
        setHosts(formatHosts(propHosts));
        return;
      }

      // Otherwise fetch them
      try {
        const hostsData = await kioskApi.getHosts(token);
        setHosts(formatHosts(hostsData));
      } catch (error) {
        console.error("Error fetching hosts:", error);
        setHosts([]);
      }
    };

    fetchHostsIfNeeded();
  }, [token, propHosts]);

  // Handle focus - scroll to field
  const handleFieldFocus = (e: React.FocusEvent) => {
    const target = e.target as HTMLElement;
    const formItem = target.closest(".ant-form-item");

    if (formItem) {
      // Small delay to ensure keyboard is up on mobile
      setTimeout(() => {
        formItem.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  };

  // Pre-populate initial values for scheduled visitor (only once)
  useEffect(() => {
    if (!hasInitializedForm) {
      // Check if there's already saved data in sessionStorage
      const STORAGE_KEY = "selfSignIn";
      const savedData = sessionStorage.getItem(STORAGE_KEY);
      let formValues: any = {};

      // First priority: Check for saved form data in localStorage
      if (savedData) {
        try {
          const data = JSON.parse(savedData);
          const savedFormData = data.forms?.visitDetails;
          if (savedFormData && Object.keys(savedFormData).length > 0) {
            // Only restore non-empty values
            Object.keys(savedFormData).forEach((key) => {
              const value = savedFormData[key];
              if (value !== "" && value !== null && value !== undefined) {
                formValues[key] = value;
              }
            });
          }
        } catch (error) {
          // Ignore localStorage errors
        }
      }

      // Second priority: Use scheduled visitor data if no localStorage data
      if (selectedScheduledVisitor && Object.keys(formValues).length === 0) {
        formValues = {};

        // Only add fields with actual values (don't set empty strings)
        if (selectedScheduledVisitor.email) {
          formValues.email = selectedScheduledVisitor.email;
        }
        if (selectedScheduledVisitor.company) {
          formValues.company = selectedScheduledVisitor.company;
        }
        if (selectedScheduledVisitor.phone) {
          formValues.phone = selectedScheduledVisitor.phone;
        }
        if (selectedScheduledVisitor.purpose) {
          formValues.purpose = selectedScheduledVisitor.purpose;
        }

        // Only set host if the hostId exists in hosts list
        if (selectedScheduledVisitor.hostId) {
          const hostIdStr = selectedScheduledVisitor.hostId.toString();
          const hostExists = hosts.some((host) => host.value === hostIdStr);

          if (hostExists) {
            formValues.host = hostIdStr;
          }
        }

        // Add custom fields if available (only non-empty values)
        if (selectedScheduledVisitor.customFields) {
          Object.keys(selectedScheduledVisitor.customFields).forEach((key) => {
            const value = selectedScheduledVisitor.customFields[key];
            if (value !== "" && value !== null && value !== undefined) {
              formValues[key] = value;
            }
          });
        }
      }

      // Apply the values if we have any
      if (Object.keys(formValues).length > 0) {
        setInitialValues(formValues);
        form.setFieldsValue(formValues);
      }

      setHasInitializedForm(true);
    }
  }, [hasInitializedForm, selectedScheduledVisitor, hosts, form]);

  // Render custom fields dynamically
  const renderCustomFields = () => {
    // Filter by included, then sort by order
    const filteredAndSortedFields = workflowInFieldsCustom
      .filter((formField) => formField.included === 1)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    return filteredAndSortedFields.map((formField) => {
      const isRequired = formField.required === 1;
      const rules = isRequired
        ? [
            {
              required: true,
              message: t("selfSignIn.visitDetails.customFieldRequired", {
                fieldName: formField.name,
              }),
            },
          ]
        : [];

      // If field has predefined values, render as Select (dropdown)
      if (formField.values && formField.values.length > 0) {
        const options = formField.values.map((option: any) => ({
          label: option.display || option.name,
          value: option.value,
        }));

        return (
          <OpForm.Select
            key={formField.id}
            label={formField.name}
            name={`customField_${formField.id}`}
            options={options}
            rules={rules}
            placeholder={
              formField.placeholder ||
              t("selfSignIn.visitDetails.customFieldSelectPlaceholder", {
                fieldName: formField.name,
              })
            }
            allowClear
          />
        );
      } else {
        return (
          <OpForm.Input
            key={formField.id}
            label={formField.name}
            name={`customField_${formField.id}`}
            rules={rules}
            placeholder={
              formField.placeholder ||
              t("selfSignIn.visitDetails.customFieldPlaceholder", {
                fieldName: formField.name,
              })
            }
          />
        );
      }
    });
  };

  return (
    <div
      className="workflow-container"
      ref={formContainerRef}
      onFocus={handleFieldFocus}
    >
      <OpForm
        form={form}
        layout="vertical"
        className="visit-details-form"
        onSubmit={() => {}}
        hasError={false}
        defaultButtons={false}
        initialValues={initialValues}
      >
        {/* Email field */}
        {workflowInFields?.email?.included === 1 && (
          <OpForm.Input
            name="email"
            label={t("selfSignIn.visitDetails.email")}
            placeholder={t("selfSignIn.visitDetails.emailPlaceholder")}
            type="email"
            rules={
              workflowInFields.email.required === 1
                ? [
                    {
                      required: true,
                      message: t("selfSignIn.visitDetails.emailRequired"),
                    },
                    {
                      type: "email",
                      message: t("selfSignIn.visitDetails.validEmail"),
                    },
                  ]
                : [
                    {
                      type: "email",
                      message: t("selfSignIn.visitDetails.validEmail"),
                    },
                  ]
            }
          />
        )}

        {/* Company field */}
        {workflowInFields?.visitCompany?.included === 1 && (
          <OpForm.Input
            name="company"
            label={t("selfSignIn.visitDetails.company")}
            placeholder={t("selfSignIn.visitDetails.companyPlaceholder")}
            rules={
              workflowInFields.visitCompany.required === 1
                ? [
                    {
                      required: true,
                      message: t("selfSignIn.visitDetails.companyRequired"),
                    },
                  ]
                : []
            }
          />
        )}

        {/* Phone field */}
        {workflowInFields?.visitPhone?.included === 1 && (
          <OpForm.PhoneInput
            name="phone"
            label={t("selfSignIn.visitDetails.phone")}
            placeholder={t("selfSignIn.visitDetails.phonePlaceholder")}
            rules={
              workflowInFields.visitPhone.required === 1
                ? [
                    {
                      required: true,
                      message: t("selfSignIn.visitDetails.phoneRequired"),
                    },
                  ]
                : []
            }
          />
        )}

        {/* Host field */}
        {workflowInFields?.host?.included === 1 && (
          <OpForm.Select
            name="host"
            label={t("selfSignIn.visitDetails.host")}
            placeholder={t("selfSignIn.visitDetails.hostPlaceholder")}
            options={hosts}
            notFoundContent={
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("common.noData")}
              />
            }
            rules={
              workflowInFields.host.required === 1
                ? [
                    {
                      required: true,
                      message: t("selfSignIn.visitDetails.hostRequired"),
                    },
                  ]
                : []
            }
            showSearch
            filterOption={(input, option) => {
              const label = option?.label;
              return (
                typeof label === "string" &&
                label.toLowerCase().includes(input.toLowerCase())
              );
            }}
            allowClear
          />
        )}

        {/* Purpose field */}
        {workflowInFields?.visitPurpose?.included === 1 && (
          <OpForm.TextAreaInput
            name="purpose"
            label={t("selfSignIn.visitDetails.purpose")}
            placeholder={t("selfSignIn.visitDetails.purposePlaceholder")}
            rows={3}
            rules={
              workflowInFields.visitPurpose.required === 1
                ? [
                    {
                      required: true,
                      message: t("selfSignIn.visitDetails.purposeRequired"),
                    },
                  ]
                : []
            }
          />
        )}

        {/* Custom fields */}
        {renderCustomFields()}
      </OpForm>
    </div>
  );
};

export default VisitDetailsPage;

// Handler function to be called by parent component
export const handleVisitDetailsNext = async (
  form: FormInstance,
): Promise<{ values: any; customFieldsData: any[] }> => {
  // Note: Validation is now handled in parent component (SelfSignIn.tsx)
  // This function just extracts and processes the form values
  const values = form.getFieldsValue();

  // Extract custom field values
  const customFieldsData: any[] = [];
  Object.keys(values).forEach((key) => {
    if (key.startsWith("customField_")) {
      const fieldId = key.replace("customField_", "");
      customFieldsData.push({
        fieldId: parseInt(fieldId),
        value: values[key],
      });
    }
  });

  return { values, customFieldsData };
};
